/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';
import { useDebouncedCallback } from 'use-debounce';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

export const messages: {
	scrollLeft: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	scrollRight: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	scrollLeft: {
		id: 'search_ai.knowledge_carousel.scroll_left',
		defaultMessage: 'Scroll left',
		description: 'Label for the scroll left button',
	},
	scrollRight: {
		id: 'search_ai.knowledge_carousel.scroll_right',
		defaultMessage: 'Scroll right',
		description: 'Label for the scroll right button',
	},
});

const styles = cssMap({
	scrollContainerStyle: {
		alignItems: 'stretch',
		display: 'flex',
		gap: token('space.200'),
		overflowX: 'auto',
		scrollbarWidth: 'none',
		whiteSpace: 'nowrap',
	},
});

const isScrollable = (elem: HTMLDivElement | null) =>
	elem ? elem.scrollWidth > elem.clientWidth : false;
const isScrollAtLeftEdge = (elem: HTMLDivElement | null) => (elem ? elem.scrollLeft <= 1 : false);
const isScrollAtRightEdge = (elem: HTMLDivElement | null) =>
	elem ? elem?.scrollWidth - elem?.scrollLeft - elem?.clientWidth <= 1 : false;

interface Props {
	children: ReactNode;
	heading: ReactNode | string;
	scrollOffset: number;
}

export const KnowledgeCarousel: ({ children, heading, scrollOffset }: Props) => JSX.Element = ({
	children,
	heading,
	scrollOffset,
}: Props) => {
	const { formatMessage } = useIntl();
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const [isContentScrollable, setIsContentScrollable] = useState(false);
	const [shouldDisableLeftArrowButton, setShouldDisableLeftArrowButton] = useState(true);
	const [shouldDisableRightArrowButton, setShouldDisableRightArrowButton] = useState(false);

	useEffect(() => {
		setIsContentScrollable(isScrollable(scrollContainerRef.current));
		setShouldDisableLeftArrowButton(isScrollAtLeftEdge(scrollContainerRef.current));
		setShouldDisableRightArrowButton(isScrollAtRightEdge(scrollContainerRef.current));
	}, []);

	const [onScroll] = useDebouncedCallback(() => {
		setShouldDisableLeftArrowButton(isScrollAtLeftEdge(scrollContainerRef.current));
		setShouldDisableRightArrowButton(isScrollAtRightEdge(scrollContainerRef.current));
	}, 10);

	const scrollLeft = () => {
		setShouldDisableRightArrowButton(false);
		scrollContainerRef.current?.scrollBy({
			top: 0,
			left: -scrollOffset,
			behavior: 'smooth',
		});
	};

	const scrollRight = () => {
		setShouldDisableLeftArrowButton(false);
		scrollContainerRef.current?.scrollBy({
			top: 0,
			left: scrollOffset,
			behavior: 'smooth',
		});
	};

	return (
		<Stack space="space.100">
			<Inline spread="space-between" alignBlock="center">
				{typeof heading === 'string' ? (
					<Text size="large" weight="semibold">
						{heading}
					</Text>
				) : (
					heading
				)}
				<Inline space="space.050">
					{isContentScrollable ? (
						<Fragment>
							<IconButton
								shape="circle"
								appearance="subtle"
								icon={ArrowLeftIcon}
								isTooltipDisabled
								label={formatMessage(messages.scrollLeft)}
								onClick={scrollLeft}
								spacing="compact"
								isDisabled={shouldDisableLeftArrowButton}
							/>
							<IconButton
								shape="circle"
								appearance="subtle"
								icon={ArrowRightIcon}
								isTooltipDisabled
								label={formatMessage(messages.scrollRight)}
								onClick={scrollRight}
								spacing="compact"
								isDisabled={shouldDisableRightArrowButton}
							/>
						</Fragment>
					) : null}
				</Inline>
			</Inline>

			<Box ref={scrollContainerRef} onScroll={onScroll} xcss={styles.scrollContainerStyle}>
				{children}
			</Box>
		</Stack>
	);
};
